import { create } from 'zustand';
import { UserService } from '../services/userService';
import { AuthStatus } from '../types/enums/AuthStatus';
import { User } from '../types/User';
import {
  getAuthToken,
  getUserData,
  removeAuthToken,
  removeUserData,
  setAuthToken,
  setUserData,
} from '../utils/storage';
import { UserStatus } from '../types/enums/UserStatus';

export interface AuthStore {
  user: User | null;
  status: AuthStatus;
  ssoToken: string | null;
  ssoLogoutToken: string | null;
}

export interface AuthActions {
  authenticate: (token: string, user: User, storeToken?: string) => void;
  unauthenticate: () => void;
  changeUserRole: () => Promise<void>;
  uploadUserImage: (image: string) => void;
  removeUserImage: () => void;
  updateUser: (user: User) => void;
  blockUser: () => void;
  unblockUser: () => void;
  setSsoToken: (token: string) => void;
  removeSsoToken: () => void;
  setSsoLogoutToken: (token: string) => void;
  removeSsoLogoutToken: () => void;
}

const authToken = getAuthToken();
const userData = getUserData();

export const useAuthStore = create<AuthStore & AuthActions>((set, get) => ({
  user: authToken && userData ? userData : null,
  status:
    authToken && userData
      ? AuthStatus.Authenticated
      : AuthStatus.Unauthenticated,
  ssoToken: null,
  ssoLogoutToken: null,
  authenticate: (token: string, user: User, storeToken?: string) => {
    setAuthToken(token);
    setUserData(user);
    set({ user, status: AuthStatus.Authenticated });
  },
  unauthenticate: () => {
    removeAuthToken();
    removeUserData();
    set({ user: undefined, status: AuthStatus.Unauthenticated });
  },
  changeUserRole: async () => {
    const userService = new UserService();
    const userRole = await userService.getUserRole();
    const user = get().user;
    if (user && userRole && userRole !== user.role) {
      set({ user: user.copyWith({ role: userRole }) });
    }
  },
  uploadUserImage: (image: string) => {
    const user = get().user;
    if (user) {
      set({ user: user.copyWith({ avatar: image }) });
    }
  },
  removeUserImage: () => {
    const user = get().user;
    if (user) {
      set({ user: user.copyWith({ avatar: '' }) });
    }
  },
  updateUser: (user: User) => {
    set({ user });
  },
  blockUser: () => {
    const user = get().user;
    if (user) {
      set({ user: user.copyWith({ status: UserStatus.Blocked }) });
    }
  },
  unblockUser: () => {
    const user = get().user;
    if (user) {
      set({ user: user.copyWith({ status: UserStatus.Active }) });
    }
  },
  setSsoToken: (token: string) => {
    set({ ssoToken: token });
  },
  removeSsoToken: () => {
    set({ ssoToken: null });
  },
  setSsoLogoutToken: (token: string) => {
    set({ ssoLogoutToken: token });
  },
  removeSsoLogoutToken: () => {
    set({ ssoLogoutToken: null });
  },
}));
